function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import { useEffect, useState } from 'react';
import cn from 'classnames';
import Decimal from 'decimal.js';
import { Controller, useFormContext } from 'react-hook-form';
import { MenuItem, Select, Tooltip, Typography, useStateInRef } from 'seagull';
import { activeAgreementCannotModifiyTriggerTooltip, serviceBillingTriggerOptions, stagesTermsTextMap } from "../../../business-logic/billingLogic";
import { useFeatureFlags } from "../../../hooks/useFeatureFlags";
import { StagesTerms, serviceModel } from "../../../models";
import { isFromAmendment } from "../../../models/service/selectors";
import { ServiceCostType } from "../../../models/service-cost";
import { BillingTriggerType } from "../../../types/api.model";
import { connectControllerToTextField } from "../../../utils/react-hook-form.utils";
import { BillingStagesSelect } from "./BillingStagesSelect/BillingStagesSelect";
import styles from "./BillingTriggerDropdown.module.scss";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
function getBillingTriggerOptions(_ref) {
  let {
    data,
    serviceOrigin,
    isExistingService,
    featureFlags
  } = _ref;
  const {
    recurrencePeriod,
    pricing,
    trigger
  } = data;
  const pricingTypeMap = {
    range: ServiceCostType.Range,
    fixed: ServiceCostType.Fixed,
    unit: ServiceCostType.Variable,
    hour: ServiceCostType.Variable
  };
  const billingTriggerMap = {
    ongoing: BillingTriggerType.Ongoing,
    approval: BillingTriggerType.OnApproval,
    manualFixed: BillingTriggerType.OneTimeManual,
    manualVariable: BillingTriggerType.OneTimeManual,
    oneTimeInStages: BillingTriggerType.OneTimeInStages
  };
  return serviceBillingTriggerOptions({
    featureFlags,
    isExistingService,
    isFromAgreement: serviceOrigin === 'agreement',
    recurrencePeriod: recurrencePeriod === 'onetime' || recurrencePeriod === 'inStages' ? null : recurrencePeriod,
    pricingType: pricingTypeMap[pricing],
    billingTrigger: billingTriggerMap[trigger]
  });
}
export default function BillingTriggerDropdown(props) {
  const {
    serviceOrigin,
    service,
    stageClassName,
    stagesClassName,
    effectiveDate
  } = props;
  const getProps = useStateInRef(props);
  const {
    control,
    watch,
    getValues,
    setValue
  } = useFormContext();
  const [recurrencePeriod, pricing, oneTimeInStagesBilling, price, discount] = watch(['recurrencePeriod', 'pricing', 'oneTimeInStagesBilling', 'price', 'discount']);
  const [triggerDropdownRef, setTriggerDropdownRef] = useState(null);
  const [stagedBillingBehaviourRef, setStagedBillingBehaviourRef] = useState(null);
  const featureFlags = useFeatureFlags();
  const [options, setOptions] = useState(() => getBillingTriggerOptions({
    data: getValues(),
    serviceOrigin,
    isExistingService: !!service,
    featureFlags
  }));
  const isApprovedServiceInAgreement = !!(serviceOrigin === 'agreement' && service && !serviceModel.isAwaitingApproval(service.status));
  useEffect(() => {
    const {
      service,
      serviceOrigin
    } = getProps();
    const newOptions = getBillingTriggerOptions({
      data: getValues(),
      serviceOrigin,
      isExistingService: !!service,
      featureFlags
    });
    setOptions(newOptions);
    if (newOptions.every(opt => opt.value !== getValues().trigger)) setValue('trigger', newOptions[0].value);
  }, [pricing, recurrencePeriod, featureFlags]);
  const stagedBillingBehaviourOptions = [{
    value: StagesTerms.AutoFirst,
    text: stagesTermsTextMap[StagesTerms.AutoFirst],
    subtext: 'First stage will be automatically billed on agreement approval. Remaining stages are billed manually, according to the service terms.'
  }, {
    value: StagesTerms.AllManual,
    text: stagesTermsTextMap[StagesTerms.AllManual],
    subtext: 'Stages require a manual trigger to initiate billing.'
  }];
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [recurrencePeriod !== 'inStages' && /*#__PURE__*/_jsx(Controller, {
      control: control,
      name: "trigger",
      render: renderProps => /*#__PURE__*/_jsx(Tooltip, {
        title: isApprovedServiceInAgreement ? activeAgreementCannotModifiyTriggerTooltip : '',
        children: /*#__PURE__*/_jsx(Select, _objectSpread(_objectSpread({
          collapseHelperText: false,
          ref: setTriggerDropdownRef
        }, connectControllerToTextField(renderProps)), {}, {
          disabled: isApprovedServiceInAgreement,
          label: "Billing trigger",
          "data-testid": "sb-trigger",
          SelectProps: {
            renderValue: value => {
              const selected = options.find(opt => opt.value === value);
              return selected ? selected.text : '';
            }
          },
          children: options.map((_ref2, idx) => {
            let {
              value,
              text,
              subtext
            } = _ref2;
            return /*#__PURE__*/_jsxs(MenuItem, {
              value: value,
              variant: "multiline",
              "data-testid": "sb-trigger-opt-".concat(idx),
              style: {
                width: triggerDropdownRef === null || triggerDropdownRef === void 0 ? void 0 : triggerDropdownRef.clientWidth
              },
              children: [/*#__PURE__*/_jsx(Typography, {
                weight: "normal",
                children: text
              }), /*#__PURE__*/_jsx(Typography, {
                children: subtext
              })]
            }, value);
          })
        }))
      })
    }), !!oneTimeInStagesBilling && /*#__PURE__*/_jsxs("div", {
      className: cn(styles.stages, stagesClassName),
      children: [/*#__PURE__*/_jsx(Select, {
        collapseHelperText: false,
        label: "Staged billing behaviour",
        autoFocus: true,
        ref: setStagedBillingBehaviourRef,
        value: oneTimeInStagesBilling.terms(),
        "data-testid": "staged-billing-behaviour",
        SelectProps: {
          renderValue: value => {
            const selected = stagedBillingBehaviourOptions.find(opt => opt.value === value);
            return selected ? selected.text : '';
          }
        },
        onChange: e => {
          setValue('oneTimeInStagesBilling', oneTimeInStagesBilling.setTerms(e.target.value), {
            shouldDirty: true
          });
        },
        children: stagedBillingBehaviourOptions.map((_ref3, idx) => {
          let {
            value,
            text,
            subtext
          } = _ref3;
          return /*#__PURE__*/_jsxs(MenuItem, {
            value: value,
            variant: "multiline",
            "data-testid": "staged-billing-behaviour-opt-".concat(idx),
            style: {
              width: stagedBillingBehaviourRef === null || stagedBillingBehaviourRef === void 0 ? void 0 : stagedBillingBehaviourRef.clientWidth
            },
            children: [/*#__PURE__*/_jsx(Typography, {
              weight: "normal",
              children: text
            }), /*#__PURE__*/_jsx(Typography, {
              children: subtext
            })]
          }, value);
        })
      }), /*#__PURE__*/_jsx(BillingStagesSelect, {
        totalRate: new Decimal(price || 0),
        effectiveDate: effectiveDate,
        discount: new Decimal(discount.amount || 0),
        stageClassName: stageClassName,
        oneTimeInStagesBilling: oneTimeInStagesBilling,
        setOneTimeInStagesBilling: oneTimeInStagesBilling => {
          setValue('oneTimeInStagesBilling', oneTimeInStagesBilling, {
            shouldDirty: true
          });
        },
        isAmendment: service ? isFromAmendment(service) : false
      })]
    })]
  });
}